// externals
import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// libraries
import type { SelectInputProps } from '@interstate/components/SelectInput';
import type { ComboBoxOptions, ComboBoxProps, ComboBoxOption } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputSize } from '@interstate/components/TextInput';

// utils
import { insertInjectedIcon } from './dropdownDomInjection';

// styles
import { ComboBoxContainer, GROUP_HEADER_ITEM_CSS, CONTENT_WRAPPER_CSS, DESCRIPTION_CSS } from './Dropdown.style';

export interface DropdownOptionWithDescription {
    value: string;
    label: string;
    description?: string;
    // `groupHeader` — built-in Interstate SelectInput option property (used by Dropdown)
    // `isGroupHeader` — custom flag for ComboBox (used by DropdownWithDescription) to apply header styling via injectDescriptions()
    // Both are set on header items so the same options array works for either component.
    isGroupHeader?: boolean;
    groupHeader?: boolean;
    hasIcon?: boolean;
}

type DropdownWithDescriptionProps = Omit<SelectInputProps, 'value' | 'options'> &
    Omit<ComboBoxProps, 'value' | 'onChange' | 'onBlur' | 'options'> & {
        value: string | undefined;
        options: DropdownOptionWithDescription[];
        allowRemoval?: boolean;
        minWidthForOptions?: string;
        size?: TextInputSize;
        placeholder?: string;
    };

export const DropdownWithDescription: FC<DropdownWithDescriptionProps> = ({
    value,
    minWidthForOptions,
    allowRemoval = false,
    options,
    sx,
    ...commonProps
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const previousSelectedValueRef = useRef<string | undefined>(value);
    const [displayValue, setDisplayValue] = useState(value);
    const componentInstanceId = useRef<string>(
        `dropdown-with-description-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    );

    // Sync displayValue with external value prop changes
    useEffect(() => {
        /* istanbul ignore next */
        if (value !== displayValue) {
            setDisplayValue(value);
        }
        previousSelectedValueRef.current = value;
    }, [value, displayValue]);

    // Convert value to ComboBox format
    const getComboBoxValue = useMemo((): ComboBoxOptions | undefined => {
        if (!displayValue) return undefined;
        const selectedOption = options.find((obj) => obj.value === displayValue);
        return selectedOption?.label ? [selectedOption as ComboBoxOption] : undefined;
    }, [displayValue, options]);

    // Convert ComboBox event to string value
    const getComboboxValueFromEvent = (e: InterstateOnChangeEvent<ComboBoxOptions>): string => {
        return e.target.value && e.target.value.length > 0 ? e.target.value[0].value : '';
    };

    const handleComboBoxChange = (e: InterstateOnChangeEvent<string | ComboBoxOptions>) => {
        if (!commonProps.onChange || e.domEvent) return;

        const selectedValue = getComboboxValueFromEvent(e as InterstateOnChangeEvent<ComboBoxOptions>);
        setDisplayValue(selectedValue);
        e.target.value = selectedValue;

        if (allowRemoval || selectedValue) {
            commonProps.onChange(e as InterstateOnChangeEvent<string>);
        }
    };

    const handleComboBoxBlur = (e: InterstateOnChangeEvent<string | ComboBoxOptions>) => {
        let selectedValue = getComboboxValueFromEvent(e as InterstateOnChangeEvent<ComboBoxOptions>);

        // When allowRemoval is false; If a value is removed, we reset back to the previous value if it exists to prevent user from removing selection.
        if (!allowRemoval && !selectedValue && previousSelectedValueRef.current) {
            selectedValue = previousSelectedValueRef.current;
            setDisplayValue(previousSelectedValueRef.current);
            e.target.value = selectedValue;
        } else {
            e.target.value = selectedValue;
        }
        commonProps.onBlur?.(e as InterstateOnChangeEvent<string>);
    };

    // Find the popover associated with this specific ComboBox instance
    const findAssociatedPopover = useCallback((): HTMLElement | null => {
        /* istanbul ignore next */
        if (!containerRef.current) return null;

        // Find the ComboBox input within this container
        const comboBoxInput = containerRef.current.querySelector<HTMLElement>(
            'input[id*="ids-combo-box-input"], input[data-testid*="combo-box"]'
        );

        if (!comboBoxInput) return null;

        // Try to find popover by aria attributes or data attributes
        // ComboBox typically uses Popper which sets aria-describedby or data attributes
        /* istanbul ignore next */
        const inputId = comboBoxInput.id || comboBoxInput.getAttribute('data-testid');
        /* istanbul ignore next */
        if (!inputId) return null;

        // Find all poppers and check which one is associated with this input
        const poppers = document.querySelectorAll<HTMLElement>('.base-Popper-root, .MuiPopper-root');
        for (const popper of Array.from(poppers)) {
            // Check if this popper contains options that match our component's options
            const listContainer = popper.querySelector<HTMLElement>('ul[role="menu"]');
            if (listContainer) {
                // Verify this popover belongs to our component by checking if it contains our options
                const firstItem = listContainer.querySelector('li[role="menuitem"]');
                if (firstItem) {
                    /* istanbul ignore next */
                    const firstItemText = firstItem.textContent?.trim() || '';
                    const hasMatchingOption = options.some((opt) => opt.label.trim() === firstItemText);
                    if (hasMatchingOption) {
                        return listContainer;
                    }
                }
            }
        }

        // Fallback: find the most recently added popover that contains a menu
        // This is less reliable but works as a last resort
        const allMenus = document.querySelectorAll<HTMLElement>('ul[role="menu"].MuiList-root');
        let mostRecentMenu: HTMLElement | null = null;
        let mostRecentTime = 0;

        for (const menu of Array.from(allMenus)) {
            // Check if this menu is near our container (spatial relationship)
            const menuRect = menu.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const distance = Math.abs(menuRect.top - (containerRect.bottom + 5)); // 5px tolerance

            // Check if menu contains our options
            const firstItem = menu.querySelector('li[role="menuitem"]');
            if (firstItem) {
                /* istanbul ignore next */
                const firstItemText = firstItem.textContent?.trim() || '';
                const hasMatchingOption = options.some((opt) => opt.label.trim() === firstItemText);

                if (hasMatchingOption && distance < 100) {
                    // Check when this element was added to DOM
                    const timeAdded = (menu as any)._timeAdded || 0;
                    if (timeAdded > mostRecentTime) {
                        mostRecentTime = timeAdded;
                        mostRecentMenu = menu;
                    }
                }
            }
        }

        return mostRecentMenu;
    }, [options]);

    // Inject descriptions into ComboBox options after render - scoped to this component
    const injectDescriptionsForThisComponent = useCallback(() => {
        const listContainer = findAssociatedPopover();
        if (!listContainer) return;

        injectDescriptions(listContainer, options, componentInstanceId.current);
    }, [options, findAssociatedPopover]);

    useEffect(() => {
        // Use MutationObserver to watch for ComboBox dropdown opening - but only check our component
        const observer = new MutationObserver(() => {
            injectDescriptionsForThisComponent();
        });

        // Observe the entire document body for changes (where the popover is rendered)
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Also try to inject periodically when dropdown might be open
        // Reduced frequency to avoid unnecessary DOM queries
        const intervalId = setInterval(() => {
            const popover = findAssociatedPopover();
            if (popover) {
                injectDescriptionsForThisComponent();
            }
        }, 200);

        return () => {
            observer.disconnect();
            clearInterval(intervalId);
        };
    }, [injectDescriptionsForThisComponent, findAssociatedPopover]);

    // Use MutationObserver to detect when Popper is added to DOM and set min-width directly
    // Only apply to the popover associated with this component
    useEffect(() => {
        if (!minWidthForOptions) return;

        const observer = new MutationObserver(() => {
            const associatedPopover = findAssociatedPopover();
            if (associatedPopover) {
                // Find the parent popper element
                const popper = associatedPopover.closest<HTMLElement>('.base-Popper-root, .MuiPopper-root');
                if (popper && popper.style.minWidth !== minWidthForOptions) {
                    popper.style.minWidth = minWidthForOptions;
                }
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => {
            observer.disconnect();
        };
    }, [minWidthForOptions, findAssociatedPopover]);

    // Convert options to ComboBox format (without description)
    const comboBoxOptions = useMemo(() => {
        return options.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.isGroupHeader || opt.groupHeader
        }));
    }, [options]);

    return (
        <ComboBoxContainer
            ref={containerRef}
            data-testid={`${commonProps['data-testid']}-container`}
            data-component-instance-id={componentInstanceId.current}
        >
            <ComboBox
                {...commonProps}
                options={comboBoxOptions}
                value={getComboBoxValue}
                onChange={handleComboBoxChange}
                allowNewEntry={false}
                onBlur={handleComboBoxBlur}
                sx={{
                    '& .ids-combo-box-multi-select-input': {
                        padding: 0
                    },
                    '& .ids-combo-box-input-and-chips': {
                        height: '32px !important',
                        minHeight: '32px !important',
                        maxHeight: '29px !important'
                    },
                    '& .ids-combo-box-input': {
                        height: '32px',
                        minHeight: '32px',
                        maxHeight: '32px'
                    },
                    '& .MuiInputBase-root': {
                        height: '32px',
                        minHeight: '32px',
                        maxHeight: '32px'
                    },
                    '& input': {
                        height: '32px',
                        minHeight: '32px',
                        padding: '4px 0px 5px',
                        lineHeight: '1.4375em'
                    },
                    ...sx
                }}
                required={commonProps.required && !displayValue}
            />
        </ComboBoxContainer>
    );
};

/**
 * Injects descriptions into ComboBox dropdown options.
 * This function is scoped to a specific list container to avoid affecting other dropdowns.
 *
 * @param listContainer - The specific ul[role="menu"] element to modify
 * @param options - The options array with descriptions to inject
 * @param componentInstanceId - Unique identifier for this component instance to prevent cross-contamination
 */
function injectDescriptions(
    listContainer: HTMLElement,
    options: DropdownOptionWithDescription[],
    componentInstanceId: string
): void {
    /* istanbul ignore next */
    if (!listContainer) return;

    // Mark this container with the component instance ID to avoid processing by other instances
    const containerEl = listContainer as HTMLElement;
    if (!containerEl.dataset.componentInstanceId) {
        containerEl.dataset.componentInstanceId = componentInstanceId;
    } else if (containerEl.dataset.componentInstanceId !== componentInstanceId) {
        // This container belongs to a different component instance, skip it
        return;
    }

    const listItems = listContainer.querySelectorAll('li[role="menuitem"]');
    listItems.forEach((item) => {
        // Check if description already injected for this component instance
        const injectedDescription = item.querySelector<HTMLElement>(
            `.dropdown-description-injected[data-component-instance-id="${componentInstanceId}"]`
        );
        /* istanbul ignore next */
        if (injectedDescription) return;

        const idsItem = item.querySelector('.ids-list-box-item');
        if (!idsItem) return;

        // Get option value from item id or data-testid
        // Format: "listbox-item-{value}" or similar
        const itemId = item.getAttribute('id') || item.getAttribute('data-testid') || '';
        const optionValueFromId = itemId.replace(/^listbox-item-?/i, '');

        // Get label text from the ids-list-box-item
        // The label is the text node directly in idsItem, not in child divs
        const labelText = Array.from(idsItem.childNodes)
            .filter((node) => {
                if (node.nodeType === Node.TEXT_NODE) return true;
                const el = node as HTMLElement;
                // Skip divs that contain SVG (checkmark) or already injected descriptions
                return (
                    el.tagName !== 'DIV' || (!el.classList.contains('dropdown-description-injected') && !el.querySelector('svg'))
                );
            })
            .map((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    /* istanbul ignore next */
                    return node.textContent?.trim();
                }
                return null;
            })
            .filter(Boolean)
            .join(' ')
            .trim();

        // Find matching option - try by id first, then by label
        const matchingOption = options.find((opt) => {
            // Match by value from id if available
            if (optionValueFromId) {
                const optValueUpper = opt.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                const idValueUpper = optionValueFromId.toUpperCase().replace(/[^A-Z0-9]/g, '');
                if (
                    optValueUpper === idValueUpper ||
                    optValueUpper.includes(idValueUpper) ||
                    idValueUpper.includes(optValueUpper)
                ) {
                    return true;
                }
            }
            // Fallback: match by label text
            return opt.label.trim() === labelText;
        });

        // Group headers are non-interactive section labels (e.g., "Approved", "Other Lenders").
        // Style the <li> as a visual header, hide the ComboBox checkmark icon (not applicable
        // for headers)
        /* istanbul ignore next */
        if (matchingOption?.isGroupHeader || matchingOption?.groupHeader) {
            (item as HTMLElement).style.cssText = GROUP_HEADER_ITEM_CSS;

            const headerCheckIcon = idsItem.querySelector<SVGElement>('svg');
            if (headerCheckIcon) {
                headerCheckIcon.style.display = 'none';
            }

            const headerMarker = document.createElement('div');
            headerMarker.className = 'dropdown-description-injected';
            headerMarker.dataset.componentInstanceId = componentInstanceId;
            headerMarker.style.display = 'none';
            idsItem.appendChild(headerMarker);
            return;
        }

        // Ensure idsItem has display: flex with row layout (checkmark on left, content on right)
        const idsItemEl = idsItem as HTMLElement;
        // Override computed style to ensure row layout
        idsItemEl.style.display = 'flex';
        idsItemEl.style.flexDirection = 'row';
        idsItemEl.style.alignItems = 'flex-start';
        idsItemEl.style.gap = '8px';

        if (matchingOption?.hasIcon) {
            insertInjectedIcon(idsItemEl, componentInstanceId);
        }

        /* istanbul ignore next */
        if (matchingOption?.description) {
            // Wrap label text in a div for better column layout
            const textNodes: Text[] = [];
            Array.from(idsItem.childNodes).forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    /* istanbul ignore next */
                    const text = node.textContent?.trim();
                    if (text && text.length > 0) {
                        textNodes.push(node as Text);
                    }
                }
            });

            // Create a content wrapper div to hold label and description in column
            let contentWrapper = idsItem.querySelector('.dropdown-content-wrapper') as HTMLElement;

            if (!contentWrapper) {
                // Create content wrapper for label and description (column layout)
                contentWrapper = document.createElement('div');
                contentWrapper.className = 'dropdown-content-wrapper';
                contentWrapper.style.cssText = CONTENT_WRAPPER_CSS;

                // Wrap label text nodes in a div
                if (textNodes.length > 0) {
                    const labelWrapper = document.createElement('div');
                    labelWrapper.className = 'dropdown-label-wrapper';

                    // Collect all text content and move to wrapper
                    let labelText = '';
                    textNodes.forEach((node) => {
                        /* istanbul ignore next */
                        labelText += node.textContent || '';
                        node.remove();
                    });
                    labelWrapper.textContent = labelText.trim();

                    contentWrapper.appendChild(labelWrapper);
                }

                // Find where to insert - after the checkmark div (or the injected icon, if present)
                let insertAfterEl: HTMLElement | null = idsItem.querySelector<HTMLElement>('.dropdown-icon-injected');
                if (!insertAfterEl) {
                    const svgElement = idsItem.querySelector('svg.interstate-checkmark-solid-icon');
                    const checkmarkDiv = svgElement ? (svgElement.closest('div') as HTMLElement) : null;
                    insertAfterEl = checkmarkDiv && checkmarkDiv.parentNode === idsItem ? checkmarkDiv : null;
                }

                if (insertAfterEl) {
                    // Insert content wrapper after the checkmark / injected icon
                    insertAfterEl.insertAdjacentElement('afterend', contentWrapper);
                } else {
                    // If no checkmark, insert at the beginning
                    idsItem.insertBefore(contentWrapper, idsItem.firstChild);
                }
            }

            // Create description element with component instance ID
            const descriptionDiv = document.createElement('div');
            descriptionDiv.className = 'dropdown-description-injected';
            descriptionDiv.dataset.componentInstanceId = componentInstanceId;
            descriptionDiv.style.cssText = DESCRIPTION_CSS;
            descriptionDiv.textContent = matchingOption.description;

            // Insert description into content wrapper (after label)
            const labelWrapper = contentWrapper.querySelector('.dropdown-label-wrapper');
            if (labelWrapper) {
                // Insert description right after the label wrapper
                labelWrapper.insertAdjacentElement('afterend', descriptionDiv);
            } else {
                // If no label wrapper, append to content wrapper
                contentWrapper.appendChild(descriptionDiv);
            }
        }
    });
}
